import React, { memo, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../../store/store';
import { type CartItem } from '../../../types/common';
import { wishListHandler } from '../../../store/UserSlice';
import ActionLinkButton from '../../UI/ActionLinkButton/ActionLinkButton';
import SidePopup from '../../shared/SidePopup/SidePopup';

import Gallery from '../../ProductSlugPage/components/Gallery/Gallery';
import AddToCartControl from '../../shared/AddToCartControl/AddToCartControl';
import Action from '../../shared/Action/Action';

import { PATHS } from '../../../constants/routes';
import './productCard.scss';

type ImageId = string;
export type ImagesObject = {
    image: ImageId;
    secondImage?: ImageId | '';
    thirdImage?: ImageId | '';
    fourthImage?: ImageId | '';
    fifthImage?: ImageId | '';
    sixthImage?: ImageId | '';
};

export type Discount = {
    percent: number;
    discountedPrice: number;
} | null;

export type ProductCardData = {
    _id: string;
    category: {
        id: string;
        name: string;
        url: string;
    };
    discount: Discount;
    name: string;
    price: number;
    weight: number;
    fillerType: {
        id: string;
        name: string;
        url: string;
    };
    fabricType: {
        id: string;
        name: string;
        url: string;
    };
    articleNumber: string;
    brandName: string;
    nameDescription: string;
    priority: number;
    images: ImagesObject;
    group: { id: string; name: string; url: string; optionValue: string };
    slug: string;
    season: string | null;
    promoType: string;
};

export type ProductCardProps = {
    data: ProductCardData;
    isMobile?: boolean;
};

const BASE_URL = process.env.REACT_APP_SERVER_URL ?? '';

const imageKeys = ['image', 'secondImage', 'thirdImage', 'fourthImage', 'fifthImage', 'sixthImage'] as const;
type ImageKey = (typeof imageKeys)[number];

const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

const isPositiveNumber = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;
const hasDiscount = (discount: Discount): discount is NonNullable<Discount> => {
    return !!discount && (isPositiveNumber(discount.percent) || isPositiveNumber(discount.discountedPrice));
};

// === ОПТИМИЗАЦИЯ: srcset/sizes + auto-формат по возможностям клиента ===
const WIDTHS = [240, 320, 480, 640, 768, 960, 1200, 1440];
const getOptimizedUrl = (imageId: string, w: number, q = 70) => `${BASE_URL}/products/images/${imageId}?w=${w}&fmt=webp&q=${q}`;
const extractImageIds = (images: ImagesObject): string[] => imageKeys.map((k: ImageKey) => images[k]).filter(isNonEmptyString) as string[];
const normalizeImages = (p: unknown): ImagesObject => {
    const anyP = p as Record<string, unknown>;
    const maybeImages = (anyP.images as Record<string, unknown> | null | undefined) ?? null;
    const pick = (k: ImageKey): string => {
        const fromNested = maybeImages?.[k];
        if (isNonEmptyString(fromNested)) return fromNested;
        const fromTop = anyP[k];
        if (isNonEmptyString(fromTop)) return fromTop;
        return '';
    };
    return {
        image: pick('image'),
        secondImage: pick('secondImage'),
        thirdImage: pick('thirdImage'),
        fourthImage: pick('fourthImage'),
        fifthImage: pick('fifthImage'),
        sixthImage: pick('sixthImage'),
    };
};
const makeSrcSet = (imageId: string) => WIDTHS.map((w) => `${getOptimizedUrl(imageId, w)} ${w}w`).join(', ');
const sizesAttr = '(min-width: 1280px) 25vw, (min-width: 768px) 33vw, 50vw';
// === конец блока оптимизации ===

// ===== Варианты (размер/цвет) =====
const parseSize = (val: unknown): [number, number] | null => {
    if (typeof val !== 'string') return null;
    const s = val.trim().toLowerCase().replace(',', '.');
    const m = s.match(/(\d+(?:\.\d+)?)\s*[xх×*]\s*(\d+(?:\.\d+)?)/i);
    if (m) {
        const w = parseFloat(m[1]);
        const h = parseFloat(m[2]);
        if (Number.isFinite(w) && Number.isFinite(h)) return [w, h];
    }
    const m1 = s.match(/^\s*(\d+(?:\.\d+)?)\s*$/);
    if (m1) {
        const w = parseFloat(m1[1]);
        if (Number.isFinite(w)) return [w, 0];
    }
    return null;
};

const collator = new Intl.Collator('ru', { numeric: true, sensitivity: 'base' });
const compareVariants = (a: ProductCardData, b: ProductCardData) => {
    const aStr = String(a.group?.optionValue ?? a.name ?? '').trim();
    const bStr = String(b.group?.optionValue ?? b.name ?? '').trim();
    const aSize = parseSize(aStr);
    const bSize = parseSize(bStr);
    if (aSize && bSize) {
        if (aSize[0] !== bSize[0]) return aSize[0] - bSize[0];
        if (aSize[1] !== bSize[1]) return aSize[1] - bSize[1];
        return 0;
    }
    return collator.compare(aStr, bStr);
};

const stopAll = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
};

const stopPropagationOnly = (e: React.SyntheticEvent) => {
    e.stopPropagation();
};
// ===== конец блока вариантов ===== //

const getImageUrlById = (imageId: string) => `${BASE_URL}/products/images/${imageId}`;

const buildHref = (p: ProductCardData) => {
    if (p.slug) return `/${p.slug}`;
    if (p.category?.url) return `${PATHS.showcase}/${p.category.url}/${p._id}`;
    return '#';
};

const ProductCard: React.FC<ProductCardProps> = ({ data }) => {
    const { name, price, images, nameDescription, discount, slug, category, _id, promoType } = data;

    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    const EMPTY_WISHLIST: string[] = [];
    const wishlist = useSelector((state: RootState) => {
        const w = state.user?.wishlist as unknown;
        return Array.isArray(w) ? (w as string[]) : EMPTY_WISHLIST;
    });

    const handleWishlist = (id: ProductCardData['_id']) => {
        const isWished = wishlist.includes(id);
        dispatch(wishListHandler({ id, isWished }));
    };

    const [isBuySidePopupOpen, setIsBuySidePopupOpen] = useState(false);
    const openBuySidePopup = useCallback(() => {
        setIsBuySidePopupOpen(true);
    }, []);
    const closeBuySidePopup = useCallback(() => {
        setIsBuySidePopupOpen(false);
    }, []);

    const imageIds = useMemo(() => extractImageIds(images), [images]);
    const cardImageIds = useMemo(() => imageIds.slice(0, 2), [imageIds]);

    const hoverSwap = cardImageIds.length > 1;

    const href = slug ? `/${slug}` : category?.url ? `${PATHS.showcase}/${category.url}/${_id}` : '#';

    const goToProduct = useCallback(() => {
        if (!href || href === '#') return;
        navigate(href);
    }, [href, navigate]);

    const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            goToProduct();
        }
    };

    // ====== ВАРИАНТЫ (размер/цвет) для SidePopup ======
    const { products } = useSelector((state: RootState) => state.product);

    const groupId = data.group?.id;
    const groupLabel = (data.group?.url || data.group?.name || 'Вариант').trim();

    const allVariants = useMemo(() => {
        if (!groupId) return [];
        const list = (products as unknown as Array<Partial<ProductCardData> & { id?: string; _id?: string; slug?: string; group?: unknown }>) || [];

        const normalized: ProductCardData[] = [];
        for (const p of list) {
            const pid = (p._id ?? p.id) as string | undefined;
            const g = p.group as { id?: string; optionValue?: string; url?: string; name?: string } | undefined;
            if (!pid || !g?.id) continue;
            if (g.id !== groupId) continue;

            normalized.push({
                _id: pid,
                slug: String(p.slug ?? ''),
                name: String(p.name ?? ''),
                nameDescription: String(p.nameDescription ?? ''),
                price: Number(p.price ?? 0),
                weight: Number(p.weight ?? 0),
                discount: (p.discount ?? null) as Discount,
                images: normalizeImages(p),
                articleNumber: String(p.articleNumber ?? ''),
                brandName: String(p.brandName ?? ''),
                priority: Number(p.priority ?? 0),
                category: (p.category ?? { id: '', name: '', url: '' }) as ProductCardData['category'],
                fillerType: (p.fillerType ?? { id: '', name: '', url: '' }) as ProductCardData['fillerType'],
                fabricType: (p.fabricType ?? { id: '', name: '', url: '' }) as ProductCardData['fabricType'],
                group: (p.group ??
                    ({
                        id: groupId,
                        name: groupLabel,
                        url: groupLabel,
                        optionValue: '',
                    } as unknown)) as ProductCardData['group'],
                season: (p.season ?? null) as string | null,
                promoType: String(p.promoType ?? 'blank'),
            });
        }

        return normalized.sort(compareVariants);
    }, [groupId, groupLabel, products]);

    const hasVariants = allVariants.length > 1;

    const [popupProduct, setPopupProduct] = useState<ProductCardData>(data);

    const [isSwitchingVariant, setIsSwitchingVariant] = useState(false);
    const switchTokenRef = useRef(0);
    const preloadVariantImages = useCallback(async (p: ProductCardData) => {
        const ids = extractImageIds(p.images).slice(0, 2);
        const urls = ids.map(getImageUrlById);
        await Promise.all(
            urls.map(
                (src) =>
                    new Promise<void>((resolve) => {
                        const img = new Image();
                        img.onload = () => resolve();
                        img.onerror = () => resolve();
                        img.src = src;
                    }),
            ),
        );
    }, []);
    useEffect(() => {
        if (!isBuySidePopupOpen) return;
        setPopupProduct(data);
        setIsSwitchingVariant(false);
        switchTokenRef.current += 1;
    }, [isBuySidePopupOpen, data, data._id]);

    const popupHref = useMemo(() => buildHref(popupProduct), [popupProduct]);
    const productTo = popupHref !== '#' ? popupHref : null;

    const popupUnitPrice = useMemo(
        () => (hasDiscount(popupProduct.discount) ? popupProduct.discount.discountedPrice : popupProduct.price),
        [popupProduct.discount, popupProduct.price],
    );

    const popupCartItem = useMemo((): CartItem => {
        const quantity = 1;
        return {
            productId: popupProduct._id,
            name: popupProduct.name,
            nameDescription: popupProduct.nameDescription || undefined,
            price: popupUnitPrice,
            quantity,
            totalPrice: popupUnitPrice * quantity,
            weight: popupProduct.weight,
            totalWeight: popupProduct.weight * quantity,
            articleNumber: popupProduct.articleNumber || undefined,
            group: popupProduct.group || undefined,
            discount: hasDiscount(popupProduct.discount) ? popupProduct.discount.percent : undefined,
            discountedPrice: hasDiscount(popupProduct.discount) ? popupProduct.discount.discountedPrice : undefined,
        };
    }, [
        popupProduct._id,
        popupProduct.name,
        popupProduct.nameDescription,
        popupUnitPrice,
        popupProduct.weight,
        popupProduct.articleNumber,
        popupProduct.group,
        popupProduct.discount,
    ]);

    const imagesFull = useMemo(() => {
        const ids = extractImageIds(popupProduct.images);
        return ids.map(getImageUrlById);
    }, [popupProduct.images]);

    const [isVariantOpen, setIsVariantOpen] = useState(false);
    const variantBtnRef = useRef<HTMLButtonElement | null>(null);

    const selectorText = useMemo(() => {
        const v = popupProduct.group?.optionValue?.trim();
        return v && v.length ? v : groupLabel;
    }, [popupProduct.group?.optionValue, groupLabel]);

    useEffect(() => {
        if (!isVariantOpen) return;

        const onDocDown = (e: MouseEvent) => {
            const t = e.target as Node | null;
            if (!t) return;

            if (variantBtnRef.current?.contains(t)) return;

            const root = (variantBtnRef.current?.closest('.productCard__sidePopup_content_variantSelect') ?? null) as HTMLElement | null;
            if (root && root.contains(t)) return;

            setIsVariantOpen(false);
        };

        const onEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsVariantOpen(false);
        };

        document.addEventListener('mousedown', onDocDown, true);
        document.addEventListener('keydown', onEsc);
        return () => {
            document.removeEventListener('mousedown', onDocDown, true);
            document.removeEventListener('keydown', onEsc);
        };
    }, [isVariantOpen]);

    const onSelectVariant = useCallback(
        async (v: ProductCardData) => {
            if (!v?._id) return;
            if (v._id === popupProduct._id) return;
            setIsVariantOpen(false);
            setIsSwitchingVariant(true);
            const token = ++switchTokenRef.current;
            try {
                await preloadVariantImages(v);
            } finally {
                if (switchTokenRef.current !== token) return;
                setPopupProduct(v);
                setIsSwitchingVariant(false);
            }
        },
        [popupProduct._id, preloadVariantImages],
    );
    // ====== конец вариантов ====== //

    return (
        <>
            <article
                className="productCard"
                data-slug={slug}
                role="link"
                tabIndex={0}
                onClick={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.closest('a') || target.closest('button')) {
                        return;
                    }
                    goToProduct();
                }}
                onKeyDown={onKeyDown}
            >
                <div
                    className="productCard__media"
                    onMouseEnter={() => {
                        if (hoverSwap) setActiveImageIndex(1);
                    }}
                    onMouseLeave={() => setActiveImageIndex(0)}
                >
                    {cardImageIds.length > 0 ? (
                        cardImageIds.map((imgId, index) => (
                            <img
                                key={index}
                                src={getOptimizedUrl(imgId, 320)}
                                srcSet={makeSrcSet(imgId)}
                                sizes={sizesAttr}
                                alt={name}
                                className={`productCard__media_image ${index === activeImageIndex ? '' : 'productCard__media_image_hidden'}`}
                                loading="lazy"
                                decoding="async"
                            />
                        ))
                    ) : (
                        <div className="productCard__img productCard__img--empty" aria-label="no image" />
                    )}

                    <div className="productCard__media_promoBox">
                        <button
                            className="productCard__media_promoBox_favouriteBtn"
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleWishlist(_id);
                            }}
                        >
                            <img
                                className="productCard__media_promoBox_favouriteBtn_img"
                                src={wishlist.includes(_id) ? '/icons/new/like_active.svg' : '/icons/new/like_disactive.svg'}
                                alt="Избранное"
                            />
                        </button>

                        {promoType !== 'blank' && (
                            <span className="productCard__media_promoBox_topRight">Sale{hasDiscount(discount) && ` -${discount.percent}%`}</span>
                        )}
                    </div>
                </div>

                <div className="productCard__body">
                    <div className="productCard__body_main">
                        <h3 className="productCard__body_main_title" title={name.length > 30 ? name : ''}>
                            {name}
                        </h3>

                        <div className="productCard__body_wrapper">
                            {nameDescription && <p className="productCard__body_main_subTitle">{nameDescription}</p>}

                            {/* <ActionLinkButton
                                className="productCard__body_main_cartBtn"
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openBuySidePopup();
                                }}
                                onKeyDown={(e) => {
                                    e.stopPropagation();
                                    if (e.key !== 'Enter' && e.key !== ' ') return;
                                    e.preventDefault();
                                    openBuySidePopup();
                                }}
                                iconAlt="Купить"
                                iconSrc="/icons/new/cart_plus.svg"
                            /> */}
                        </div>
                    </div>

                    <div className="productCard__body_wrapper">
                        <div className="productCard__body_price">
                            {hasDiscount(discount) && <div className="productCard__body_price_currentCost">{discount.discountedPrice.toLocaleString()} ₽</div>}
                            {hasDiscount(discount) ? (
                                <div className="productCard__body_price_previousCost">{price.toLocaleString()} ₽</div>
                            ) : (
                                <div className="productCard__body_price_cost">{price.toLocaleString()} ₽</div>
                            )}
                        </div>
                        <ActionLinkButton
                            className="productCard__body_main_cartBtn"
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openBuySidePopup();
                            }}
                            onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key !== 'Enter' && e.key !== ' ') return;
                                e.preventDefault();
                                openBuySidePopup();
                            }}
                            iconAlt="Купить"
                            iconSrc="/icons/new/cart_plus.svg"
                        />
                    </div>
                </div>
            </article>

            <SidePopup className="productCard__sidePopup" open={isBuySidePopupOpen} onClose={closeBuySidePopup} closeOnOverlayClick closeOnEsc lockBodyScroll>
                <div className={`productCard__sidePopup_content${isSwitchingVariant ? ' productCard__sidePopup_content--switching' : ''}`}>
                    <div className="productCard__sidePopup_content_switchOverlay" aria-hidden="true" />
                    <Gallery
                        // key={popupProduct._id}
                        images={imagesFull}
                        wrapperClassName="productCard__sidePopup_content_gallery_wrapper"
                        imageClassName="productCard__sidePopup_content_gallery_image"
                        arrowClassName="productCard__sidePopup_content_gallery_arrowButton"
                        indicatorClassName="productCard__sidePopup_content_gallery_indicator"
                        indicatorActiveClassName="productCard__sidePopup_content_gallery_indicator--active"
                        ariaLabel="Галерея товара"
                        autoPlay={false}
                        showArrows
                        showIndicators
                    />
                    <div className="productCard__sidePopup_content_wrapper">
                        <div className="productCard__sidePopup_content_box">
                            <h3 className="productCard__sidePopup_content_title">{popupProduct.name}</h3>
                            <button
                                className="productCard__sidePopup_content_favouriteBtn"
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleWishlist(popupProduct._id);
                                }}
                            >
                                <img
                                    className="productCard__sidePopup_content_favouriteBtn_img"
                                    src={wishlist.includes(popupProduct._id) ? '/icons/new/like_active_black.svg' : '/icons/new/like_disactive_black.svg'}
                                    alt="Избранное"
                                />
                            </button>
                        </div>
                        {popupProduct.nameDescription && <p className="productCard__sidePopup_content_subTitle">{popupProduct.nameDescription}</p>}

                        <div className="productCard__sidePopup_content_box">
                            <div className="productCard__sidePopup_content_price">
                                {hasDiscount(popupProduct.discount) && (
                                    <div className="productCard__sidePopup_content_price_currentCost">
                                        {popupProduct.discount.discountedPrice.toLocaleString()} ₽
                                    </div>
                                )}
                                {hasDiscount(popupProduct.discount) ? (
                                    <div className="productCard__sidePopup_content_price_previousCost">{popupProduct.price.toLocaleString()} ₽</div>
                                ) : (
                                    <div className="productCard__sidePopup_content_price_cost">{popupProduct.price.toLocaleString()} ₽</div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="productCard__sidePopup_content_wrapper">
                        {/* <div className="productCard__sidePopup_content_box"> */}
                        {hasVariants && (
                            <div className="productCard__sidePopup_content_variantSelect" onMouseDown={stopPropagationOnly} onClick={stopPropagationOnly}>
                                <button
                                    ref={variantBtnRef}
                                    type="button"
                                    className={`productCard__sidePopup_content_variantSelect_btn ${
                                        isVariantOpen ? 'productCard__sidePopup_content_variantSelect_btn--open' : ''
                                    }`}
                                    aria-haspopup="listbox"
                                    aria-expanded={isVariantOpen}
                                    onClick={(e) => {
                                        stopAll(e);
                                        setIsVariantOpen((v) => !v);
                                    }}
                                    onKeyDown={(e) => {
                                        e.stopPropagation();
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setIsVariantOpen((v) => !v);
                                        }
                                        if (e.key === 'Escape') setIsVariantOpen(false);
                                    }}
                                    title={`${groupLabel}: ${selectorText}`}
                                    disabled={isSwitchingVariant}
                                >
                                    <span className="productCard__sidePopup_content_variantSelect_value">{groupLabel}</span>
                                    <span className="productCard__sidePopup_content_variantSelect_value">{selectorText}</span>
                                    <span className="productCard__sidePopup_content_variantSelect_chev" aria-hidden="true" />
                                </button>

                                {isVariantOpen && (
                                    <div className="productCard__sidePopup_content_variantSelect_menu" role="listbox" aria-label={groupLabel}>
                                        {allVariants.map((v) => {
                                            const isActive = v._id === popupProduct._id;
                                            const label = (v.group?.optionValue?.trim() || groupLabel).toString();
                                            return (
                                                <button
                                                    key={v._id}
                                                    type="button"
                                                    className={`productCard__sidePopup_content_variantSelect_option ${
                                                        isActive ? 'productCard__sidePopup_content_variantSelect_option--active' : ''
                                                    }`}
                                                    role="option"
                                                    aria-selected={isActive}
                                                    disabled={isActive || isSwitchingVariant}
                                                    onClick={(e) => {
                                                        stopAll(e);
                                                        onSelectVariant(v);
                                                    }}
                                                    title={label}
                                                >
                                                    <span className="productCard__sidePopup_content_variantSelect_optionText">{label}</span>

                                                    {isActive && <span className="productCard__sidePopup_content_variantSelect_tick" aria-hidden="true" />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        <AddToCartControl
                            product={popupCartItem}
                            addActionExtraClass="productCard__sidePopup_content_cartBtn"
                            quantityBackground="rgb(244, 243, 240)"
                            disableDecrement={false}
                        />
                        {/* </div> */}

                        <Action
                            as="link"
                            to={productTo ?? '/'}
                            onClick={() => {
                                closeBuySidePopup();
                            }}
                            variant="outline"
                            extraClass="productCard__sidePopup_content_prodLink"
                            disabled={!productTo}
                        >
                            Узнать больше
                        </Action>
                    </div>
                </div>
            </SidePopup>
        </>
    );
};

export default memo(ProductCard);
